import api from '@/api'
import Logger from '@/logger'
import { EmptyFeatureValue, FeatureDoesNotExists, NoActiveClassification, NoActiveFeature } from '../exceptions'

const logger = new Logger('store/featureRules/actions')
const SELLER_ID = process.env.VUE_APP_NER_CLASSIFICATIONS_SELLER_ID
const DATE = process.env.VUE_APP_NER_CLASSIFICATIONS_DATE

if (!SELLER_ID) throw new Error('Please provide the VUE_APP_NER_CLASSIFICATIONS_SELLER_ID env variable')
if (!DATE) throw new Error('Please provide the VUE_APP_NER_CLASSIFICATIONS_DATE env variable')

/**
 * Show a snackbar as a notification indicating an error.
 * @param {Function} commit to commit a mutation
 * @param {String} message the message to show
 */
function notifyError(commit, message) {
  commit('notifications/notify', { type: 'error', message }, { root: true })
}


/**
 * Show a snackbar as a notification indicating any successful message.
 * @param {Function} commit to commit a mutation
 * @param {String} message the message to show
 */
function notifySuccess(commit, message) {
  commit('notifications/notify', { type: 'success', message }, { root: true })
}


/**
 * Create a new feature for the current activate classification.
 * @param {Object} context
 * @param {String} name The name to set to the new feature.
 */
function createFeature({ commit, state }, name) {
  if (!state.classification) throw new NoActiveClassification()

  const classification = state.classification.id
  commit('setLoadingCreateFeature', true)
  return api.features.create({ name, classification }).then(feature => {
    const features = [...state.features, feature]
    commit('setFeatures', features)
    commit('setLoadingCreateFeature', false)
    return feature
  }).catch(err => {
    logger.error(err)
    commit('setLoadingCreateFeature', false)
    notifyError(commit, 'Tuvimos un problema creando el atributo')
  })
}


/**
 * Get the brands of the selected classification.
 * @param {Object} context
 */
function getBrands({ commit, state }) {
  if (!state.classification) throw new NoActiveClassification()
  commit('setLoadingBrands', true)
  return api.brands.getByClassificationId(state.classification.id).then(brands => {
    commit('setBrands', brands)
  }).catch(err => {
    logger.error(err)
    notifyError(commit, 'No pudimos cargar las marcas')
  }).then(() => {
    commit('setLoadingBrands', false)
  })
}


/**
 * Get and set the classifications.
 * @param {Object} context
 */
function getClassifications({ commit }) {
  commit('setLoadingClassifications', true)
  return api.classifications.get({ seller: SELLER_ID, date: DATE }).then(classifications => {
    commit('setClassifications', classifications)
  }).catch(err => {
    logger.error(err)
    notifyError(commit, 'Tuvimos un problema cargando las clasificaciones')
  }).then(() => {
    commit('setLoadingClassifications', false)
  })
}


/**
 * Get and set the features of the current classification
 * @param {Object} context
 */
function getFeatures({ commit, state }) {
  const classification = state.classification
  if (!classification) throw new NoActiveClassification()
  commit('setLoadingFeatures', true)
  return api.features.get(classification.id).then(features => {
    commit('setFeatures', features)
  }).catch(err => {
    logger.error(err);
    notifyError(commit, `Tuvimos un problema obteniendo los atributos de la clase "${classification.name}"`)
  }).then(() => {
    commit('setLoadingFeatures', false)
  })
}


/**
 * Get the distinct values of the selected feature that have been used.
 * @param {Object} context
 */
function getFeatureValues({ commit, state }) {
  if (!state.feature) throw new NoActiveFeature()
  commit('setLoadingFeatureValues', true)
  return api.features.getDistinctValues(state.feature.id).then(values => {
    commit('setFeatureValues', values)
  }).catch(err => {
    logger.error(err)
    notifyError(commit, `Lo siento, no pudimos cargar los valores del atributo "${state.feature.name}"`)
  }).then(() => {
    commit('setLoadingFeatureValues', false)
  })
}


/**
 * Get the items that match the options of the settings and the options of the pagination object.
 * For more information see the docs of the endpoint in the matcher api.
 * @param {Object} context
 */
function getItems({ commit, state }) {
  if (!state.classification) throw new NoActiveClassification()
  if (!state.feature) throw new NoActiveFeature()
  const { descending, page, rowsPerPage, sortBy } = state.itemsPagination
  commit('setLoadingItems', true)
  return api.matchingItems.getByRule({
    brands: state.selectedBrands,
    classificationId: state.classification.id,
    featureId: state.feature.id,
    likePattern: state.likePattern.like,
    notLikePattern: state.likePattern.notLike,
    onlyMissing: state.onlyMissing,
    offset: (page - 1) * rowsPerPage,
    fetch: rowsPerPage,
    orderBy: sortBy,
    descending
  }).then(({ total, items }) => {
    commit('setItems', items)
    commit('setItemsTotal', total)
    commit('setActiveTab', 1) // Show the tab's content with the items
    commit('setFeatureValue', null)
  }).catch(err => {
    logger.error(err)
    notifyError(commit, 'Lo siento, no podemos cargar los MatchingItems')
  }).then(() => {
    commit('setLoadingItems', false)
  })
}


/**
 * Get the rules already created for the selected classification and feature.
 * @param {Object} context
 */
function getRules({ commit, state }) {
  if (!state.classification) throw new NoActiveClassification()
  if (!state.feature) throw new NoActiveFeature()
  commit('setLoadingRules', true)
  const { page, rowsPerPage } = state.rulesPagination
  return api.featureRules.get({
    classificationId: state.classification.id,
    featureId: state.feature.id,
    offset: (page - 1) * rowsPerPage,
    fetch: rowsPerPage
  }).then(({ total, rules }) => {
    commit('setRules', rules)
    commit('setRulesTotal', total)
  }).catch(err => {
    logger.error(err);
    notifyError(commit, 'No pudimos cargar las reglas creadas anteriormente')
  }).then(() => {
    commit('setLoadingRules', false)
  })
}


/**
 * Remove a feature from the database and from the store.
 * @param {Object} context
 * @param {String} name The name of the feature to delete.
 */
function removeFeature({ commit, state }, id) {
  if (!state.classification) throw new NoActiveClassification()

  const features = state.features.filter(f => f.id !== id)
  if (features.length === state.features) {
    throw new FeatureDoesNotExists()
  }

  commit('setLoadingRemoveFeature', true)
  return api.features.remove({ id }).then(() => {
    commit('setFeatures', features)
    commit('setLoadingRemoveFeature', false)
  }).catch(err => {
    commit('setLoadingRemoveFeature', false)
    logger.error(err)
    notifyError(commit, `Tuvimos un problema eliminando el atributo`)
  })
}


/**
 * Delete a rule by its id.
 * @param {Object} context
 * @param {Number} id
 */
function removeRule({ commit, dispatch, state }, id) {
  return api.featureRules.remove(id).then(() => {
    const rules = state.rules.filter(r => r.id !== id);
    commit('setRules', rules);
    notifySuccess(commit, 'Regla eliminada con éxito')
    dispatch('getFeatureValues')
  }).catch(err => {
    logger.error(err);
    notifyError(commit, 'No pudimos eliminar la regla en este momento')
  })
}


/**
 * Save a rule with all its configurations and add the feature to all the matching items.
 * @param {Object} context
 */
function saveRule({ commit, dispatch, state }) {
  if (!state.classification) throw new NoActiveClassification()
  if (!state.feature) throw new NoActiveFeature()
  if (!state.featureValue) throw new EmptyFeatureValue()

  commit('setLoadingSaveRule', true)
  return api.featureRules.create({
    classificationId: state.classification.id,
    featureId: state.feature.id,
    value: state.featureValue,
    likePattern: state.likePattern.like,
    notLikePattern: state.likePattern.notLike,
    brands: state.selectedBrands
  })
    .then(() => {
      notifySuccess(commit, 'Regla creada y atributos añadidos con éxito')
      dispatch('getRules')
      dispatch('getItems')
      dispatch('getFeatureValues')
      commit('setFeatureValue', null)
    })
    .catch(err => {
      logger.error(err)
      notifyError(commit, 'Lo siento, no pudimos crear la regla')
    }).then(() => {
      commit('setLoadingSaveRule', false)
    })
}


/**
 * Set the selected classification and trigger the `getFeatures` and `getBrands` actions.
 * 
 * @param {Object} context
 * @param {Number} id the id of the classification to set as selected.
 */
function setClassification({ commit, dispatch, state }, id) {
  commit('setClassification', id)
  if (state.classification) {
    dispatch('getFeatures')
    dispatch('getBrands')
  }
}


/**
 * Set the selected feature by its id and trigger the action to get its unique values.
 * @param {Object} context
 * @param {Number} id
 */
function setFeature({ commit, dispatch, state }, id) {
  commit('setFeature', id)
  if (state.feature) {
    dispatch('getFeatureValues')
    dispatch('getRules')
  }
}


/**
 * WARNING: THIS IS NOT AN ACTION. Is a useful function for the actions that sets the pagination
 * of other tables.
 * 
 * Given the old pagination object with a new one we want to check that it's consistent
 * and return a new object.
 * When could it be not consistent? When it changes the rowsPerPage, the sortBy or the
 * descending properties. Those ones changes the map between page-item, so an item that was
 * on the first page before the sortBy change could be in the 5th page after, so we need to
 * validate this, construct a new object and reset the page to go to the first one.
 * 
 * @param {Object} oldPagination
 * @param {Object} newPagination
 */
function getNewPaginationObject(oldPagination, { descending, page, rowsPerPage, sortBy }) {
  /* Start from an empty pagination object and we'll build the new one */
  const newPagination = {}

  /* Check if the sortBy or the descending properties are changed and start from the first page if it's the case */
  let startFromFirstPage = false

  /* Check if the sortBy property is given and that is different from the old one */
  if (sortBy && sortBy !== oldPagination.sortBy) {
    startFromFirstPage = true
    newPagination.sortBy = sortBy
  } else {
    newPagination.sortBy = oldPagination.sortBy
  }

  /* Same with the descending property */
  if ((descending === false || descending === true) && descending !== oldPagination.descending) {
    startFromFirstPage = true
    newPagination.descending = descending
  } else {
    newPagination.descending = oldPagination.descending
  }

  /* If sortBy or descending has changed we are going to get the first page because the order of the items
   * has changed, so to avoid conflicts and misunderstanding of the data we are going to start again */
  if (startFromFirstPage) {
    newPagination.page = 1
  } else {
    /* If the sortBy and descending properties are the same, update the page if given */
    newPagination.page = page ? page : oldPagination.page
  }

  /* Update the rowsPerPage if given */
  newPagination.rowsPerPage = rowsPerPage ? rowsPerPage : oldPagination.rowsPerPage

  return newPagination
}


/**
 * Set the pagination object that controls the items table.
 * This will dispatch the `getItems` action according to the new pagination object.
 * If the `sortBy` or `descending` properties changes it will load again the first
 * page. 
 * @param {Object} context
 * @param {Object} payload
 * @param {Object} payload.pagination the new pagination object to set
 * @param {Boolean} payload.getItems to indicate that it needs to get the items after setting
 *  the pagination. As by default is true is useful when the pagination object is being set
 *  by the first time and we already have the items, to avoid getting them again.
 */
function setItemsPagination({ commit, dispatch, state }, { pagination, getItems = true }) {
  const newPagination = getNewPaginationObject(state.itemsPagination, pagination)
  commit('setItemsPagination', newPagination)
  if (getItems) dispatch('getItems')
}


/**
 * Set the rules' pagination object and get the rules again.
 * Similar to the setItemsPagination action, see its docs.
 * @param {Object} context
 * @param {Object} payload.pagination
 * @param {Boolean} payload.getRules
 */
function setRulesPagination({ commit, dispatch, state }, { pagination, getRules = true }) {
  const newPagination = getNewPaginationObject(state.rulesPagination, pagination)
  commit('setRulesPagination', newPagination)
  if (getRules) dispatch('getRules')
}

export default {
  createFeature,
  getBrands,
  getClassifications,
  getFeatures,
  getFeatureValues,
  getItems,
  getRules,
  removeFeature,
  removeRule,
  saveRule,
  setClassification,
  setFeature,
  setItemsPagination,
  setRulesPagination
}